import { publishLatencySample } from './networkLatencyObserver';
import { extractNetworkLatency } from './resourceTiming/extractNetworkLatency';
import {
  OBSERVED_INITIATOR_TYPES,
  RESOURCE_ENTRY_TYPE,
} from './resourceTiming/resourceTiming.constants';

const noop = (): void => {};

const isObservedEntry = (entry: PerformanceEntry): boolean => {
  if (entry.entryType !== RESOURCE_ENTRY_TYPE) {
    return false;
  }

  const initiatorType = (entry as PerformanceResourceTiming).initiatorType;

  return (OBSERVED_INITIATOR_TYPES as readonly string[]).includes(
    initiatorType,
  );
};

const handleEntry = (entry: PerformanceResourceTiming): void => {
  const { networkLatencyMs, latencySource, throughputMbps, transferMs } =
    extractNetworkLatency(entry);

  if (networkLatencyMs === undefined && throughputMbps === undefined) {
    return;
  }

  publishLatencySample({
    durationMs: networkLatencyMs ?? transferMs ?? 0,
    networkLatencyMs,
    latencySource,
    throughputMbps,
    isTimeout: false,
    url: entry.name,
  });
};

const isResourceTimingSupported = (): boolean => {
  if (typeof PerformanceObserver === 'undefined') {
    return false;
  }

  const supportedEntryTypes = PerformanceObserver.supportedEntryTypes;

  if (!supportedEntryTypes) {
    return true;
  }

  return supportedEntryTypes.includes(RESOURCE_ENTRY_TYPE);
};

/**
 * Subscribes to `PerformanceResourceTiming` entries for fetch/XHR calls and
 * feeds backend-independent network latency samples into the latency observer.
 * Returns a disconnect function. No-op (and safe) in environments without
 * `PerformanceObserver` support, e.g. jsdom.
 */
export const startResourceTimingObserver = (): (() => void) => {
  if (!isResourceTimingSupported()) {
    return noop;
  }

  const observer = new PerformanceObserver((list) => {
    list
      .getEntries()
      .filter(isObservedEntry)
      .forEach((entry) => {
        handleEntry(entry as PerformanceResourceTiming);
      });
  });

  try {
    observer.observe({ type: RESOURCE_ENTRY_TYPE, buffered: true });
  } catch {
    try {
      observer.observe({ entryTypes: [RESOURCE_ENTRY_TYPE] });
    } catch {
      observer.disconnect();

      return noop;
    }
  }

  return () => {
    observer.disconnect();
  };
};
